import { Refusal } from './refusal';

/**
 * What waits for the session sends again (the family rule on a session that ends while the person
 * works): `read` once renewed, `write` once renewed while it waited, `false` never (a destructive or
 * irreversible action, which the person presses again)
 */
export type Replay = 'read' | 'write' | false;

/** `Session.lost()`: resolves with whether to send the request again */
export type Guard = (lost: { reason: string | null; replay: Replay }) => Promise<boolean>;

/**
 * The versioned transport to the Snapshots service: the only place that knows a URL, a method or an
 * error envelope. The session lives in a host-only cookie the browser sends, so requests are made with
 * credentials and this class never holds a credential of its own.
 *
 * An answer that says the session no longer serves goes to its `guard` (`Session.lost()` of
 * `@beyond-js/ui`) with the request's replay class: a read is `read`, a deletion `false`, any other
 * change `write`. The request is sent again when the guard says so; otherwise it fails with a quiet
 * refusal (`Refusal.quiet`) that no screen shows as an error. The session's own reads and the sign-out
 * are never held: they are how the session is known. A change may carry headers of the contract
 * (`Idempotency-Key`), sent again unchanged with it.
 */
export class Client {
	static PROTOCOL = 'beyond-snapshots/1';
	/**
	 * How long a call may take, body included, in milliseconds. Longer than the service's own bounds on
	 * its database and on Accounts and Projects, so the person reads the service's precise answer
	 */
	static TIMEOUT = 20000;
	/** The session's own paths, answered to whoever reads the session */
	static OWN = new Set(['/v1/session', '/v1/auth/signout']);
	/** How many times one request is sent again after renewals */
	static AGAIN = 2;

	#origin: string;
	#fetcher: typeof fetch;
	#timeout: number;
	#guard: Guard | null = null;

	constructor(origin: string, fetcher: typeof fetch = (...args) => globalThis.fetch(...args), timeout = Client.TIMEOUT) {
		this.#origin = origin.replace(/\/$/, '');
		this.#fetcher = fetcher;
		this.#timeout = timeout;
	}

	get origin() {
		return this.#origin;
	}

	/** `Session.lost()` once a session is held, or null: a lost session's refusal is then thrown as it came */
	set guard(value: Guard | null) {
		this.#guard = value;
	}

	/** The replay class of a request by its method */
	static replay(method: string): Replay {
		if (method === 'GET') return 'read';
		return method === 'DELETE' ? false : 'write';
	}

	/**
	 * @throws Refusal for any answer that is not a success, for an unreachable service, and for one that
	 * did not answer within the bound: `UNREACHABLE`, marked `uncertain` when it was asked for a change;
	 * `Refusal.quiet` for a request the ended session kept from being sent again
	 */
	async call<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
		for (let attempt = 0; ; attempt++) {
			try {
				return await this.#send<T>(method, path, body, headers);
			} catch (cause) {
				const guard = this.#guard;
				if (!(cause instanceof Refusal) || !cause.lost || !guard || Client.OWN.has(path.split('?')[0])) throw cause;
				const again = attempt < Client.AGAIN && (await guard({ reason: cause.reason, replay: Client.replay(method) }));
				if (!again) throw Refusal.quiet(cause);
			}
		}
	}

	async #send<T>(method: string, path: string, body: unknown, extra: Record<string, string>): Promise<T> {
		const signal = AbortSignal.timeout(this.#timeout);
		// A read that got no answer changed nothing. A change that got none may have been made: it timed out, its
		// connection failed (which a browser cannot tell from one that was dropped) or a server in front of the
		// service answered in its place, so only the service's own answer says that nothing was changed (D186)
		const unknown = method !== 'GET' ? { outcome: 'unknown' } : undefined;
		let response: Response;
		let text: string;
		try {
			response = await this.#fetcher(`${this.#origin}${path}`, {
				method,
				credentials: 'include',
				headers: { ...extra, ...(body === undefined ? { accept: 'application/json' } : { accept: 'application/json', 'content-type': 'application/json' }) },
				body: body === undefined ? undefined : JSON.stringify(body),
				signal
			});
			text = response.status === 204 ? '' : await response.text();
		} catch (cause) {
			throw new Refusal('UNREACHABLE', signal.aborted ? 'The service did not answer in time' : 'The service could not be reached', 0, { cause: String(cause), ...unknown });
		}
		if (response.status === 204) return undefined as T;
		let parsed: unknown;
		try {
			parsed = text ? JSON.parse(text) : undefined;
		} catch {
			throw new Refusal('UNREACHABLE', 'The service answered something unreadable', response.status, unknown);
		}
		if (!response.ok) {
			const error = (parsed as { error?: { code?: string; message?: string; details?: Record<string, unknown>; request?: string } })?.error;
			const details = error ? error.details : response.status >= 500 ? unknown : undefined;
			throw new Refusal(error?.code ?? (response.status >= 500 || response.status === 429 ? 'UPSTREAM_UNAVAILABLE' : 'INTERNAL'), error?.message ?? 'The request failed', response.status, details, typeof error?.request === 'string' ? error.request : undefined);
		}
		return parsed as T;
	}

	get<T>(path: string): Promise<T> {
		return this.call<T>('GET', path);
	}
	post<T>(path: string, body?: unknown): Promise<T> {
		return this.call<T>('POST', path, body ?? {});
	}
	patch<T>(path: string, body: unknown): Promise<T> {
		return this.call<T>('PATCH', path, body);
	}
	delete<T>(path: string): Promise<T> {
		return this.call<T>('DELETE', path);
	}
}
